import { differenceInCalendarDays } from 'date-fns';
import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { parseTimestamp } from '@/util/dateUtils';

const COMPARISON_PERIOD_KEYS: Record<number, string> = {
  0: 'analytics.compare_vs_prev_day',
  6: 'analytics.compare_vs_prev_7days',
  13: 'analytics.compare_vs_prev_14days',
};

export const getComparisonPeriodKey = (startDate: string, endDate: string): string => {
  const start = parseTimestamp(startDate);
  const end = parseTimestamp(endDate);
  if (!start || !end) {
    return 'analytics.compare_vs_prev_period';
  }
  const dayCount = differenceInCalendarDays(end, start);
  if (dayCount === 29) {
    const endsToday = differenceInCalendarDays(new Date(), end) === 0;
    return endsToday ? 'analytics.compare_vs_prev_30days' : 'analytics.compare_vs_prev_month';
  }
  return COMPARISON_PERIOD_KEYS[dayCount] ?? 'analytics.compare_vs_prev_period';
};

type TrendDirection = 'up' | 'down' | 'neutral';

export interface PercentChange {
  text: string;
  color: string;
  direction: TrendDirection;
}

export const computePercentChange = (
  current: number | null | undefined,
  previous: number | null | undefined
): PercentChange => {
  if (current == null || previous == null || previous === 0) {
    return { text: 'N/A', color: 'text-muted-foreground', direction: 'neutral' };
  }
  const change = ((current - previous) / previous) * 100;
  const rounded = Math.round(change * 10) / 10;
  if (rounded === 0) {
    return { text: '0%', color: 'text-muted-foreground', direction: 'neutral' };
  }
  const prefix = rounded > 0 ? '+' : '';
  const color = rounded > 0 ? 'text-success' : 'text-destructive';
  const direction: TrendDirection = rounded > 0 ? 'up' : 'down';
  return { text: `${prefix}${rounded}%`, color, direction };
};

const INVERTED_COLORS: Record<Exclude<TrendDirection, 'neutral'>, string> = {
  up: 'text-destructive',
  down: 'text-success',
};

type TrendIconComponent = typeof TrendingUpIcon | typeof TrendingDownIcon;

const INVERTED_ICONS: Record<Exclude<TrendDirection, 'neutral'>, TrendIconComponent> = {
  up: TrendingDownIcon,
  down: TrendingUpIcon,
};

const DEFAULT_ICONS: Record<Exclude<TrendDirection, 'neutral'>, TrendIconComponent> = {
  up: TrendingUpIcon,
  down: TrendingDownIcon,
};

const getTrendIcon = (
  direction: TrendDirection,
  invertColors: boolean | undefined
): TrendIconComponent | null => {
  if (direction === 'neutral') {
    return null;
  }
  if (invertColors) {
    return INVERTED_ICONS[direction];
  }
  return DEFAULT_ICONS[direction];
};

type Size = 'sm' | 'md';

const sizeClasses: Record<Size, { text: string; icon: string; gap: string }> = {
  sm: { text: 'text-xs', icon: 'size-3', gap: 'gap-1' },
  md: { text: 'text-sm', icon: 'size-3.5', gap: 'gap-1' },
};

interface Props {
  current: number | null | undefined;
  previous: number | null | undefined;
  comparisonLabel?: string;
  invertColors?: boolean;
  size?: Size;
  isLoading?: boolean;
  className?: string;
}

export const PercentChangeIndicator = ({
  current,
  previous,
  comparisonLabel,
  invertColors,
  size = 'sm',
  isLoading,
  className,
}: Props) => {
  if (isLoading) {
    return <Skeleton className={cn('h-4 w-24', className)} />;
  }

  const { text, color, direction } = computePercentChange(current, previous);
  const effectiveColor =
    invertColors && direction !== 'neutral' ? INVERTED_COLORS[direction] : color;
  const TrendIcon = getTrendIcon(direction, invertColors);

  const { text: textSize, icon: iconSize, gap } = sizeClasses[size];

  return (
    <div className={cn('flex items-center font-medium', gap, textSize, className)}>
      <span className={effectiveColor}>{text}</span>
      {comparisonLabel && <span className="text-muted-foreground">{comparisonLabel}</span>}
      {TrendIcon && <TrendIcon className={cn(iconSize, effectiveColor)} />}
    </div>
  );
};
